import { useEffect, useState }             from "react"
import { Button, FormControl, FormLabel, Link, TextField, Typography } from "@mui/material"
import { useLocation, useNavigate, useSearchParams } from "react-router"
import { AuthCard }                          from "./AuthCard"
import { useGlobalConfig }                   from "context/GlobalContext"
import { useToast }                          from "context/ToastContext"
import { AuthApis }                          from "apis/authAPI"
import { ANALYTICS_EVENTS }                  from "framework/analytics/AnalyticsEvents"

export const ResetPasswordComponent = () => {

  const { rc }   = useGlobalConfig()
  const toast    = useToast()
  const navigate = useNavigate()
  const location = useLocation() as { state ?: { email ?: string } }
  const [searchParams] = useSearchParams()

  // Email carried over from the forgot-password step (router state, not the URL,
  // to avoid leaking it in query strings/logs) — shown so the user can confirm
  // they entered the right address.
  const submittedEmail = location.state?.email || ''

  // Reset link from the email is `<frontend-url>?code=<token>` → the root
  // redirects here preserving ?code. The code is ALWAYS taken from the URL — it
  // is never entered by hand. Without a code we show a "check your inbox" prompt.
  const codeFromUrl = searchParams.get('code') || ''

  const [password, setPassword]                     = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [loading, setLoading]                       = useState(false)

  useEffect(() => {
    // Reset is not available to logged-in users — a signed-in user changes their
    // password from within the app, not via this flow. Bounce them home.
    if (rc.session.isUserLoggedIn()) {
      rc.uiRouter.navigateToHome()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.AUTH.RESET_PASSWORD_OPENED)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = async (event : React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!codeFromUrl.trim()) {
      toast.error('Reset code is missing. Please use the link from your email.')
      return
    }
    if (!password || password.length < 6) {
      toast.error('Password must be at least 6 characters long')
      return
    }
    if (password !== passwordConfirmation) {
      toast.error('Passwords do not match')
      return
    }

    rc.analytics.trackEvent(ANALYTICS_EVENTS.AUTH.RESET_PASSWORD_SUBMITTED)
    setLoading(true)

    const resp = await AuthApis.resetPassword(rc, codeFromUrl.trim(), password, passwordConfirmation)

    if (resp.error) {
      setLoading(false)
      rc.analytics.trackEvent(ANALYTICS_EVENTS.AUTH.RESET_PASSWORD_FAILED, {
        error_message : resp.error.message || 'reset_password_failed',
      })
      toast.error(resp.error.message || 'Could not reset password. Please check the code and try again.')
      return
    }

    setLoading(false)

    // Do NOT auto-login off the reset response — send the user to the login page
    // to sign in with their new password.
    rc.analytics.trackEvent(ANALYTICS_EVENTS.AUTH.RESET_PASSWORD_SUCCESS)
    toast.success('Password updated successfully. Please log in with your new password.')
    navigate('/auth/login', { replace : true })
  }

  // No code yet — the user landed here from "forgot password" (or directly). Tell
  // them to open the link in their inbox; the link carries the code that unlocks
  // the form below.
  if (!codeFromUrl) {
    return (
      <AuthCard title="Check your inbox">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, textAlign: 'center' }}>
          <Typography variant="bglBody1">
            {submittedEmail
              ? <>If an account exists for <b>{submittedEmail}</b>, we&apos;ve sent a password reset link. Open it from your inbox to set a new password.</>
              : <>If an account exists for that email, we&apos;ve sent a password reset link. Open it from your inbox to set a new password.</>}
          </Typography>
          <Link
            component="button"
            type="button"
            onClick={() => navigate('/auth/forgot-password')}
            sx={{ fontSize: '0.875rem', cursor: 'pointer' }}
          >
            Didn&apos;t get it? Request another reset email
          </Link>
          <Link
            component="button"
            type="button"
            onClick={() => navigate('/auth/login')}
            sx={{ fontSize: '0.875rem', cursor: 'pointer' }}
          >
            Back to login
          </Link>
        </div>
      </AuthCard>
    )
  }

  return (
    <AuthCard title="Reset Password">
      <form onSubmit={handleSubmit} noValidate autoComplete="off" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <FormControl>
          <FormLabel sx={{ textAlign: 'left' }} htmlFor="reset-password">New password</FormLabel>
          <TextField
            id="reset-password"
            name="password"
            type="password"
            placeholder="••••••"
            autoComplete="new-password"
            autoFocus
            required
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel sx={{ textAlign: 'left' }} htmlFor="reset-password-confirm">Confirm new password</FormLabel>
          <TextField
            id="reset-password-confirm"
            name="passwordConfirmation"
            type="password"
            placeholder="••••••"
            autoComplete="new-password"
            required
            fullWidth
            variant="outlined"
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
          />
        </FormControl>
        <Button type="submit" fullWidth variant="contained" loading={loading}>
          Reset password
        </Button>
      </form>
    </AuthCard>
  )
}
